import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isLoggedIn, posStaffSession } from '../../api';
import { errorText, ORDER_TYPE_LABEL, PosBill, PosKot, posApi } from './api';
import './pos.css';

const money = (n: number) => Number(n).toFixed(2);
const stamp = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' }) : '');

// Print/back bar shown on screen only. Opens the browser print dialog once the ticket has loaded.
function PrintShell({ ready, children }: { ready: boolean; children: React.ReactNode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const back = params.get('back');
  const goBack = () => navigate(back && back.startsWith('/pos') ? back : '/pos');
  useEffect(() => {
    if (!ready) return undefined;
    const t = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(t);
  }, [ready]);
  if (!isLoggedIn() && !posStaffSession()) return <Navigate to="/pos/login" replace />;
  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <div className="pos-print-bar pos">
        <button className="pos-btn" onClick={goBack}>← Back</button>
        <button className="pos-btn gold" disabled={!ready} onClick={() => window.print()}>Print</button>
      </div>
      {children}
    </div>
  );
}

// Kitchen/bar tickets: one per station that received items (?ids=1,2).
export const KotPrint: React.FC = () => {
  const [params] = useSearchParams();
  const ids = (params.get('ids') ?? '').split(',').map(Number).filter((n) => n > 0);
  const [kots, setKots] = useState<PosKot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ids.join(',');
  useEffect(() => {
    Promise.all(key.split(',').filter(Boolean).map((id) => posApi.kot(Number(id)))).then(setKots).catch((err) => setError(errorText(err)));
  }, [key]);
  return (
    <PrintShell ready={!!kots && kots.length > 0}>
      {error && <p className="pos-receipt">{error}</p>}
      {kots?.map((k, i) => (
        <div key={k.id} className="pos-receipt" style={i > 0 ? { pageBreakBefore: 'always' } : undefined}>
          <h1>{k.station === 'Bar' ? 'BOT' : 'KOT'} #{k.kotNo}</h1>
          <div className="c big">{k.tableName ? `TABLE ${k.tableName}` : ORDER_TYPE_LABEL[k.orderType].toUpperCase()}</div>
          <div className="c">Order #{k.orderNo} · {k.covers} pax</div>
          <div className="c">{stamp(k.createdAt)} · {k.createdBy}</div>
          <hr />
          <table>
            <tbody>
              {k.items.map((l, j) => (
                <tr key={j}>
                  <td className="big" style={{ width: '14mm' }}>{l.quantity} x</td>
                  <td className="big">
                    {l.isVoid ? <s>{l.name}</s> : l.name}{l.variantName ? ` (${l.variantName})` : ''}
                    {l.isVoid && <div>** VOID **</div>}
                    {l.note && <div style={{ fontWeight: 'normal' }}>&gt;&gt; {l.note}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <hr />
        </div>
      ))}
    </PrintShell>
  );
};

// The customer bill (tax invoice when the venue has a GSTIN).
export const BillPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [bill, setBill] = useState<PosBill | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { posApi.billPrint(Number(id)).then(setBill).catch((err) => setError(errorText(err, 'This order has no bill yet.'))); }, [id]);
  const o = bill?.order;
  const v = bill?.venue;
  return (
    <PrintShell ready={!!bill}>
      {error && <p className="pos-receipt">{error}</p>}
      {o && v && (
        <div className="pos-receipt">
          <h1>{v.legalName || v.venueName}</h1>
          {v.legalName && v.legalName !== v.venueName && <div className="c">({v.venueName})</div>}
          {v.billAddress && <div className="c" style={{ whiteSpace: 'pre-line' }}>{v.billAddress}</div>}
          {v.billPhone && <div className="c">Ph: {v.billPhone}</div>}
          {v.gstin && <div className="c">GSTIN: {v.gstin}</div>}
          {v.fssaiNumber && <div className="c">FSSAI: {v.fssaiNumber}</div>}
          <hr />
          <div className="c big">{v.gstin ? 'TAX INVOICE' : 'BILL'}</div>
          <table>
            <tbody>
              <tr><td>Bill: {o.billNumber}</td><td className="r">{stamp(o.billedAt)}</td></tr>
              <tr><td>{o.tableName ? `Table ${o.tableName}` : ORDER_TYPE_LABEL[o.orderType]} · {o.covers} pax</td><td className="r">Order #{o.orderNo}</td></tr>
              {o.customerName && <tr><td colSpan={2}>Guest: {o.customerName}{o.customerPhone ? ` (${o.customerPhone})` : ''}</td></tr>}
            </tbody>
          </table>
          <hr />
          <table>
            <thead><tr><td>Item</td><td className="r">Qty</td><td className="r">Rate</td><td className="r">Amt</td></tr></thead>
            <tbody>
              {o.items.filter((l) => !l.isVoid).map((l) => (
                <tr key={l.id}>
                  <td>{l.name}{l.variantName ? ` (${l.variantName})` : ''}</td>
                  <td className="r">{l.quantity}</td>
                  <td className="r">{money(l.unitPrice)}</td>
                  <td className="r">{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <hr />
          <table>
            <tbody>
              <tr><td>Subtotal</td><td className="r">{money(o.subtotal)}</td></tr>
              {o.discountAmount > 0 && <tr><td>Discount{o.discountPercent ? ` ${o.discountPercent}%` : ''}</td><td className="r">-{money(o.discountAmount)}</td></tr>}
              {o.serviceCharge > 0 && <tr><td>Service charge {o.serviceChargePercent}%</td><td className="r">{money(o.serviceCharge)}</td></tr>}
              {o.taxes.map((t) => <tr key={t.label}><td>{t.label} on {money(t.taxable)}</td><td className="r">{money(t.amount)}</td></tr>)}
              {o.roundOff !== 0 && <tr><td>Round off</td><td className="r">{money(o.roundOff)}</td></tr>}
              <tr className="big"><td>TOTAL</td><td className="r">₹{money(o.total)}</td></tr>
            </tbody>
          </table>
          {o.payments.length > 0 && (
            <>
              <hr />
              <table><tbody>{o.payments.map((p) => <tr key={p.id}><td>Paid by {p.method}</td><td className="r">{money(p.amount)}</td></tr>)}</tbody></table>
            </>
          )}
          {o.serviceCharge > 0 && <div style={{ fontSize: 10, marginTop: 4 }}>Service charge is optional; ask us to remove it.</div>}
          <hr />
          <div className="c" style={{ whiteSpace: 'pre-line' }}>{v.billFooter || 'Thank you! Visit again.'}</div>
          <div className="c" style={{ fontSize: 10, marginTop: 4 }}>Powered by C2N POS</div>
        </div>
      )}
    </PrintShell>
  );
};
